import { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'
import { format, parseISO, isToday, isBefore, isAfter, startOfDay } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { IMontagemGrupoOS } from '../../Pages/HCP/MontagemCartelas/montagem-cartelas.hook'

interface IEmProducaoTableProps {
  grupos: IMontagemGrupoOS[]
  loading: boolean
  comOSTotal?: number
  comOSCarregados?: number
  comOSHasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
}

type SortKey = 'prazo' | 'tiragem' | 'etapa'
type SortDir = 'asc' | 'desc'

function prazoLabel(prazo_producao: string): { label: string; urgente: boolean } {
  if (!prazo_producao) return { label: '—', urgente: false }
  try {
    const date = parseISO(prazo_producao)
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const urgente = isBefore(date, hoje) || isToday(date)
    return { label: format(date, 'dd/MM/yyyy', { locale: ptBR }), urgente }
  } catch {
    return { label: prazo_producao, urgente: false }
  }
}

function StatusChip({ atrasado, no_prazo }: { atrasado: boolean; no_prazo: boolean | null }) {
  if (atrasado) {
    return (
      <Chip
        label="Atrasado"
        size="small"
        sx={{ bgcolor: 'rgba(211, 47, 47, 0.12)', color: '#b71c1c', fontWeight: 600, fontSize: '0.75rem' }}
      />
    )
  }
  if (no_prazo === true) {
    return (
      <Chip
        label="No prazo"
        size="small"
        sx={{ bgcolor: 'rgba(132, 168, 2, 0.15)', color: '#4a6000', fontWeight: 600, fontSize: '0.75rem' }}
      />
    )
  }
  return (
    <Chip
      label="Sem prazo"
      size="small"
      sx={{ bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}
    />
  )
}

function OSRow({ grupo }: { grupo: IMontagemGrupoOS }) {
  const [open, setOpen] = useState(false)
  const { label: prazoFormatado, urgente } = prazoLabel(grupo.prazo_producao)

  return (
    <>
      <TableRow
        onClick={() => setOpen(!open)}
        sx={{
          '& > *': { borderBottom: 'unset' },
          bgcolor: grupo.atrasado ? 'rgba(211, 47, 47, 0.04)' : 'inherit',
          cursor: 'pointer',
          '&:hover': { bgcolor: grupo.atrasado ? 'rgba(211, 47, 47, 0.08)' : 'rgba(0,0,0,0.02)' },
        }}
      >
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open) }}>
            {open ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Typography fontWeight={700} fontSize={14} color="primary">
            OS {grupo.ordem_de_servico}
          </Typography>
          <Typography fontSize={12} color="text.secondary">{grupo.produto_nome}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontSize={14}>{grupo.itens.length}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontWeight={600} fontSize={14}>
            {grupo.totalTiragem.toLocaleString('pt-BR')}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Chip
            label={grupo.etapa || '—'}
            size="small"
            sx={{ bgcolor: 'rgba(0,0,0,0.06)', color: 'text.primary', fontSize: '0.75rem', fontWeight: 500 }}
          />
        </TableCell>

        <TableCell align="center">
          <Typography
            fontSize={14}
            fontWeight={urgente ? 700 : 400}
            color={urgente ? 'error.main' : 'text.primary'}
          >
            {prazoFormatado}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <StatusChip
            atrasado={grupo.atrasado}
            no_prazo={grupo.prazo_producao ? !grupo.atrasado : null}
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ mx: 2, my: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>#Pedido</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Cliente</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Tiragem</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Prazo</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Designer</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Arte Final</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Pend. pedido</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grupo.itens.map((item) => {
                    const prazoItem = item.producao?.prazo_producao
                    const { label: prazoItemLabel, urgente: prazoUrgente } = prazoItem
                      ? prazoLabel(prazoItem)
                      : { label: '—', urgente: false }
                    return (
                      <TableRow key={item.produto_pedido_id} hover>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{item.numero}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{item.cliente}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.85rem' }}>
                          {(item.tiragem ?? (item.producao?.tiragem ? parseInt(item.producao.tiragem, 10) : 0)).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            fontSize="0.85rem"
                            fontWeight={prazoUrgente ? 700 : 400}
                            color={prazoUrgente ? 'error.main' : 'text.primary'}
                          >
                            {prazoItemLabel}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                          {item.designer?.nome ?? '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          {item.link_arte_final ? (
                            <Link href={item.link_arte_final} target="_blank" rel="noopener noreferrer" underline="hover">
                              Arte Final
                            </Link>
                          ) : (
                            <Typography component="span" fontSize="0.85rem" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <StatusChip
                            atrasado={!!item.producao?.prazo_producao && !isAfter(parseISO(item.producao.prazo_producao), startOfDay(new Date()))}
                            no_prazo={!item.producao?.prazo_producao ? null : isAfter(parseISO(item.producao.prazo_producao), startOfDay(new Date()))}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.itens_pendentes === 0 ? 'Completo' : `${item.itens_pendentes} pend.`}
                            size="small"
                            sx={{
                              fontSize: '0.70rem', height: 20, fontWeight: 600,
                              bgcolor: item.itens_pendentes === 0 ? 'rgba(46,125,50,0.12)' : '#fff3e0',
                              color: item.itens_pendentes === 0 ? '#2e7d32' : '#e65100',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export function EmProducaoTable({
  grupos, loading,
  comOSTotal, comOSCarregados, comOSHasMore, loadingMore, onLoadMore,
}: IEmProducaoTableProps) {
  const [sortBy, setSortBy] = useState<SortKey>('prazo')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
  }

  const sortedGrupos = useMemo(() => {
    return [...grupos].sort((a, b) => {
      let cmp = 0
      if (sortBy === 'prazo') {
        const ap = a.prazo_producao ?? ''
        const bp = b.prazo_producao ?? ''
        if (!ap && !bp) cmp = 0
        else if (!ap) cmp = 1
        else if (!bp) cmp = -1
        else cmp = ap.localeCompare(bp)
      } else if (sortBy === 'tiragem') {
        cmp = a.totalTiragem - b.totalTiragem
      } else if (sortBy === 'etapa') {
        cmp = (a.etapa ?? '').localeCompare(b.etapa ?? '')
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [grupos, sortBy, sortDir])

  const atrasados = grupos.filter((g) => g.atrasado).length
  const noPrazo = grupos.filter((g) => !g.atrasado && !!g.prazo_producao).length
  const semPrazo = grupos.filter((g) => !g.prazo_producao).length

  if (!loading && grupos.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="text.secondary">Nenhuma OS em produção encontrada.</Typography>
      </Box>
    )
  }

  return (
    <>
      {/* Resumo */}
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.01)' }}>
        <Chip
          label={`${atrasados} atrasada${atrasados !== 1 ? 's' : ''}`}
          size="small"
          sx={{ bgcolor: 'rgba(211, 47, 47, 0.10)', color: '#b71c1c', fontWeight: 700, fontSize: '0.72rem' }}
        />
        <Chip
          label={`${noPrazo} no prazo`}
          size="small"
          sx={{ bgcolor: 'rgba(132, 168, 2, 0.12)', color: '#4a6000', fontWeight: 700, fontSize: '0.72rem' }}
        />
        <Chip
          label={`${semPrazo} sem prazo`}
          size="small"
          sx={{ bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary', fontWeight: 700, fontSize: '0.72rem' }}
        />
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.03)' }}>
              <TableCell sx={{ width: 48 }} />
              <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>OS / Produto</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Pedidos</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                <TableSortLabel
                  active={sortBy === 'tiragem'}
                  direction={sortBy === 'tiragem' ? sortDir : 'asc'}
                  onClick={() => handleSort('tiragem')}
                >
                  Tiragem
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                <TableSortLabel
                  active={sortBy === 'etapa'}
                  direction={sortBy === 'etapa' ? sortDir : 'asc'}
                  onClick={() => handleSort('etapa')}
                >
                  Etapa
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                <TableSortLabel
                  active={sortBy === 'prazo'}
                  direction={sortBy === 'prazo' ? sortDir : 'asc'}
                  onClick={() => handleSort('prazo')}
                >
                  Prazo
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedGrupos.map((grupo) => (
              <OSRow key={grupo.ordem_de_servico} grupo={grupo} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Ver mais */}
      {(comOSHasMore || loadingMore) && (
        <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          {comOSTotal !== undefined && comOSCarregados !== undefined && (
            <Typography variant="caption" color="text.disabled">
              {comOSCarregados} de {comOSTotal} OS carregadas
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={onLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={12} /> : undefined}
            sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
          >
            {loadingMore ? 'Carregando...' : 'Ver mais'}
          </Button>
        </Box>
      )}
    </>
  )
}
